import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
    Text,
} from '@fluentui/react-components';

// component functions
import * as ComponentFunctions from './ConceptValue.Delete.Functions';

// styles
import { useStyles } from './ConceptValues.Styles';

const fmtAmount = (amount: number | undefined): string =>
    (amount ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ConceptValueDeleteComponent = (props: ComponentFunctions.Props) => {

    const { t } = useTranslation();
    const styles = useStyles();
    const wasDeletingRef = useRef(false);

    // La fila desaparece del store cuando el borrado sale bien: eso, y no una
    // bandera de exito, es lo que cierra el dialogo.
    useEffect(() => {
        if (wasDeletingRef.current && !props.isDeleting && !props.stillExists) {
            props.onClose();
        }
        wasDeletingRef.current = props.isDeleting;
    }, [props.isDeleting, props.stillExists]);

    const HandleDelete = () => {
        if (props.ConceptValue) {
            props.DeleteConceptValue(props.ProtocolId, props.ProtocolConsumptionId, props.ConceptValue.id);
        }
    };

    return (
        <Dialog
            open={props.isOpen && props.ConceptValue !== undefined}
            onOpenChange={(_, data) => { if (!data.open && !props.isDeleting) props.onClose(); }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{t('conceptValueDelete.title')}</DialogTitle>
                    <DialogContent>
                        {props.failOnDeleting && (
                            <MessageBar intent="error" role="alert">
                                <MessageBarBody>
                                    <MessageBarTitle>{t('conceptValueDelete.errorTitle')}</MessageBarTitle>
                                    {props.errorOnDeleting?.ErrorMessage || props.errorOnDeleting?.Errors?.join(', ')}
                                </MessageBarBody>
                            </MessageBar>
                        )}
                        <p>
                            <Text>{t('conceptValueDelete.description')}</Text>
                        </p>
                        <div className={styles.deleteSummary}>
                            <div className={styles.previewRow}>
                                <span className={styles.previewLabel}>{t('conceptValueDelete.concept')}</span>
                                <span className={styles.previewValue}>{props.ConceptValue?.contractConceptName}</span>
                            </div>
                            <div className={styles.previewRow}>
                                <span className={styles.previewLabel}>{t('conceptValueDelete.grossValue')}</span>
                                <span className={styles.previewValue}>
                                    {fmtAmount(props.ConceptValue?.value)} {props.ConceptValue?.currencyCode}
                                </span>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="subtle" disabled={props.isDeleting} onClick={props.onClose}>
                            {t('conceptValueDelete.cancel')}
                        </Button>
                        <Button
                            appearance="primary"
                            disabled={props.isDeleting}
                            onClick={HandleDelete}
                            icon={props.isDeleting ? <Spinner size="extra-tiny" /> : undefined}
                        >
                            {props.isDeleting ? t('conceptValueDelete.removing') : t('conceptValueDelete.confirm')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConceptValueDeleteComponent as any);
